import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useOcSanity, useRun, useUsersSummary } from "@/api/queries";
import { useSession } from "@/features/auth/session";
import { REQUIRED_API_VERSION } from "@/lib/buildInfo";
import { runProgress } from "@/lib/cells";
import { toasts } from "@/lib/toasts";
import { useRuntime } from "./runtime";
import { ShellDataProvider, type ShellData } from "./shell/shellData";

function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function LiveShellData({ children }: { children: ReactNode }) {
  const s = useSession();
  const r = useRuntime();
  const navigate = useNavigate();
  const online = useOnline();
  const summary = useUsersSummary(s.role === "ADMIN");
  const active = useRun(s.status === "active" ? (r.activeRunId ?? "") : "", !!r.activeRunId);
  const sanity = useOcSanity();

  useEffect(() => {
    if (s.role !== "ADMIN") return;
    const t = setInterval(() => void summary.refetch(), 60_000);
    return () => clearInterval(t);
  }, [s.role, summary]);

  const value = useMemo<ShellData>(() => {
    const snap = r.activeRunId ? active.data : undefined;
    const { done, total } = snap ? runProgress(snap) : { done: 0, total: 0 };
    return {
      runtime: {
        state: r.state,
        version: r.version?.runtime_version,
        apiVersion: r.version?.api_version,
        engineVersion: r.version?.engine_version,
        instanceId: r.version?.instance_id,
        platform: r.version ? `${r.version.platform} ${r.version.architecture}` : undefined,
        degradedReason: r.health?.degraded_reason ?? undefined,
        required: REQUIRED_API_VERSION,
      },
      user: s.user ? { name: s.user.name, email: s.user.email, picture: s.user.picture, role: s.role } : null,
      activeRun: snap && snap.lifecycle !== "COMPLETED" ? { runId: snap.run_id, done, total } : null,
      pendingCount: summary.data?.waiting ?? 0,
      adminApi: s.adminApi,
      cachedRoleMinutesLeft: s.minutesLeft,
      online,
      signOut: () => void s.signOut(),
      retryRuntime: r.refetch,
      runEnvironmentCheck: () =>
        sanity.mutate(undefined, {
          onSuccess: () => navigate("/"),
          onError: () => toasts.envFailed(1, () => navigate("/")),
        }),
    };
  }, [s, r, active.data, summary.data, online, sanity, navigate]);

  return <ShellDataProvider value={value}>{children}</ShellDataProvider>;
}
